import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import { Crest } from "@/components/Crest";
import { useAuth } from "@/context/AuthContext";

const serviceMenu = [
  { href: "/#services", label: "All Services" },
  { href: "/#echo", label: "Echocardiogram" },
  { href: "/#ecg", label: "12-Lead ECG" },
  { href: "/#holter", label: "Holter Monitoring" },
  { href: "/#abpm", label: "24-hr Ambulatory BP" },
];

const links = [
  { href: "/#manifesto", label: "Charter" },
  { href: "/#services", label: "Services", dropdown: true },
  { href: "/private-gp", label: "Private GP" },
  { href: "/#partnerships", label: "Partnerships" },
  { href: "/#leadership", label: "About" },
  { href: "/contact", label: "Contact" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);
  const dark = scrolled || menuOpen;

  const linkCls = `font-mono text-[11px] tracking-[0.25em] uppercase transition-colors ${
    dark ? "text-ivory/80 hover:text-champagne" : "text-navy/70 hover:text-navy"
  }`;

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 border-b ${
        scrolled
          ? "bg-navy/90 backdrop-blur-xl border-gold/20"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="nav-brand-link" onClick={closeMenu}>
          <Crest light={dark} className="h-11" />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) =>
            l.dropdown ? (
              <div key={l.href} className="relative group">
                <a
                  href={l.href}
                  data-testid="nav-link-services"
                  className={`${linkCls} flex items-center gap-1.5 py-2`}
                >
                  {l.label}
                  <ChevronDown size={12} className="transition-transform duration-300 group-hover:rotate-180" />
                </a>
                <div
                  className="absolute left-1/2 -translate-x-1/2 top-full pt-3 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-[opacity,visibility] duration-300"
                  data-testid="nav-services-dropdown"
                >
                  <div className="bg-navy border border-gold/25 py-2 min-w-[230px] shadow-2xl">
                    {serviceMenu.map((s) => (
                      <a
                        key={s.href}
                        href={s.href}
                        data-testid={`nav-dropdown-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className="block px-5 py-2.5 font-mono text-[11px] tracking-[0.2em] uppercase text-ivory/70 hover:text-gold hover:bg-ivory/5 transition-colors"
                      >
                        {s.label}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            ) : l.label === "Private GP" ? (
              <div key={l.href} className="relative group">
                <a
                  href={l.href}
                  data-testid="nav-link-private-gp"
                  className={`${linkCls} py-2`}
                >
                  {l.label}
                </a>
                <div
                  className="absolute left-1/2 -translate-x-1/2 top-full pt-3 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-[opacity,visibility] duration-300 pointer-events-none"
                  data-testid="nav-privategp-tooltip"
                >
                  <div className="bg-navy border border-gold/25 px-5 py-4 w-72 shadow-2xl">
                    <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-gold mb-2">
                      Private GP Clinic
                    </p>
                    <p className="text-[11px] text-ivory/70 leading-relaxed">
                      On-site echocardiography, ECG, Holter and ABPM delivered inside your
                      own practice — including one-stop clinics.
                    </p>
                    <p className="mt-2.5 font-mono text-[9px] tracking-[0.25em] uppercase text-gold">
                      Click for Details →
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={linkCls}
              >
                {l.label}
              </a>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          {user ? (
            <Link
              to="/portal"
              data-testid="nav-portal-button"
              className="bg-gold text-navy font-mono text-[11px] tracking-[0.2em] uppercase px-5 py-2.5 hover:bg-champagne transition-colors"
            >
              Enter Portal
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="nav-login-link"
                className={`font-mono text-[11px] tracking-[0.2em] uppercase px-4 py-2.5 border transition-colors ${
                  dark
                    ? "border-ivory/30 text-ivory hover:border-gold hover:text-gold"
                    : "border-navy/30 text-navy hover:border-gold hover:text-gold"
                }`}
              >
                Specialist Login
              </Link>
              <Link
                to="/contact"
                data-testid="nav-request-access-button"
                className="hidden sm:block bg-gold text-navy font-mono text-[11px] tracking-[0.2em] uppercase px-5 py-2.5 hover:bg-champagne transition-colors"
              >
                Contact Us
              </Link>
            </>
          )}
          <button
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid="nav-mobile-menu-button"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            className={`md:hidden p-1.5 transition-colors ${dark ? "text-ivory" : "text-navy"}`}
          >
            {menuOpen ? <X size={22} strokeWidth={1.75} /> : <Menu size={22} strokeWidth={1.75} />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="fixed inset-0 z-40 bg-navy md:hidden" data-testid="nav-mobile-menu">
          <div className="h-full overflow-y-auto pt-28 pb-10 px-8 flex flex-col">
            <nav className="flex flex-col">
              {links.map((l) =>
                l.dropdown ? (
                  <div key={l.href}>
                    <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold/60 pt-5 pb-1">
                      Services
                    </p>
                    {serviceMenu.map((s) => (
                      <a
                        key={s.href}
                        href={s.href}
                        onClick={closeMenu}
                        data-testid={`nav-mobile-link-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className="block py-3 pl-4 font-mono text-xs tracking-[0.25em] uppercase text-ivory/70 border-b border-ivory/10"
                      >
                        {s.label}
                      </a>
                    ))}
                  </div>
                ) : (
                  <a
                    key={l.href}
                    href={l.href}
                    onClick={closeMenu}
                    data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="block py-3.5 font-mono text-xs tracking-[0.25em] uppercase text-ivory/85 border-b border-ivory/10"
                  >
                    {l.label}
                  </a>
                )
              )}
            </nav>
            <div className="mt-auto pt-8 flex flex-col gap-3">
              {user ? (
                <Link
                  to="/portal"
                  onClick={closeMenu}
                  data-testid="nav-mobile-portal-button"
                  className="bg-gold text-navy text-center font-mono text-xs tracking-[0.2em] uppercase px-5 py-4"
                >
                  Enter Portal
                </Link>
              ) : (
                <>
                  <Link
                    to="/contact"
                    onClick={closeMenu}
                    data-testid="nav-mobile-contact-button"
                    className="bg-gold text-navy text-center font-mono text-xs tracking-[0.2em] uppercase px-5 py-4"
                  >
                    Contact Us
                  </Link>
                  <Link
                    to="/login"
                    onClick={closeMenu}
                    data-testid="nav-mobile-login-button"
                    className="border border-ivory/30 text-ivory text-center font-mono text-xs tracking-[0.2em] uppercase px-5 py-4"
                  >
                    Specialist Login
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
